import './MediaChat.sass';
import { AnimatedEmoji, Header, ScrollView, Tabs, TabsItem } from '$uikit';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCurrentUserInfo, useTranslator } from '$hooks';

import { AttachItem, AttachType, Message } from '$store/models';
import { BundleAttachItem } from '$core/CreateBundle/BundleAttachItem/BundleAttachItem';
import { Loader, MediaViewer } from '$shared/components';
import { RootState } from '$store/rootReducer';
import { useDispatch, useSelector } from 'react-redux';
import { paidActions, paidSelector } from '$store/paid';
import { ListOfButtonSelect } from '$uikit/ListOfButtonSelect/ListOfButtonSelect';
import { TabId, Role } from '$core/MediaChat/MediaChat.interface';
import { tabs } from '$core/MediaChat/constants';
import { filterContentKeys } from '$core/MediaChat/helpers';
import { useRoute, useRouter } from 'react-router5';
import { CHAT } from '$shared/constants/pages';
import { useListOfSecondFilters } from '$core/MediaChat/hooks/useListOfSecondFilters';
export const MediaChat = () => {
  const [selectedTab, setSelectedTab] = useState<TabId>('all');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [media, setMedia] = useState<Message[]>([]);
  const [type, setType] = useState<AttachType | 'all'>('all');
  const [isMediaViewer, setIsMediaViewer] = useState(false);
  const [openMediaId, setOpenMediaId] = useState(0);
  const { media: initialMedia, isMediaLoading } = useSelector(paidSelector);
  const dispatch = useDispatch();
  const user = useCurrentUserInfo();
  const decryptKeys = useSelector((state: RootState) => state.bundles.decryptKeys);
  const t = useTranslator();
  const router = useRouter();
  const { route } = useRoute();
  const listOfSecondFilters = useListOfSecondFilters(initialMedia, selectedTab);
  const peerId = +route.params.peerId;
  const decryptKey = user.id ? decryptKeys[user.id] : undefined;
  const isModel = user.verified === 2;
  const role: Role = isModel ? 'model' : 'user';
  const isMedia = !isMediaLoading && media.length > 0;
  const isNothing = !isMediaLoading && media.length === 0;
  let hiddenCount = 0;

  const openMedia = useCallback(
    (id) => () => {
      setIsMediaViewer(true);
      setOpenMediaId(id);
    },
    [],
  );

  const handleTabChange = useCallback((newTab: string) => {
    setSelectedTab(newTab as TabId);
  }, []);

  useEffect(() => {
    dispatch(paidActions.loadMediaMessages(true));
    return () => {
      dispatch(paidActions.setMediaMessages(null));
    };
  }, []);

  useEffect(() => {
    if (initialMedia === null) {
      return;
    }

    const media = filterContentKeys(initialMedia[selectedTab], initialMedia.content);

    if (type === 'all') {
      setMedia(media);
      return;
    }

    setMedia(media.filter(({ attach }) => attach.type === type));
  }, [selectedTab, type, initialMedia]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [selectedTab]);

  const attachments = useMemo(
    () =>
      media.reduce((acc: AttachItem[], { attach, isHidden }) => {
        if (!isHidden) {
          acc.push(attach);
        }
        return acc;
      }, []),
    [media],
  );

  const backButtonOnClick = () => {
    router.navigate(CHAT, { peerId }, { replace: true });
  };

  return (
    <ScrollView className={'media'}>
      <Header backButtonOnClick={backButtonOnClick} showBackButton>
        {t('post_label_subscription_media')}
      </Header>
      <Tabs onChange={handleTabChange} selected={selectedTab}>
        {tabs.map(({ id, label, view }) =>
          view === role || view === 'all' ? (
            <TabsItem key={id} value={id}>
              {t(label)}
            </TabsItem>
          ) : null,
        )}
      </Tabs>
      {isMediaLoading && <Loader className={'media__loader'} />}
      {isMedia && (
        <>
          <ListOfButtonSelect<AttachType | 'all'>
            selectedIndex={selectedIndex}
            setSelectedIndex={setSelectedIndex}
            setSelect={setType}
            data={listOfSecondFilters}
          />
          <div className={'media__content'}>
            {media.map(({ messageId, attach, isHidden }, index) => {
              if (isHidden) {
                hiddenCount++;
              }

              return (
                <BundleAttachItem
                  onClick={
                    !isHidden
                      ? openMedia(index - hiddenCount)
                      : () =>
                          router.navigate(
                            CHAT,
                            { peerId, scrollTo: `${messageId}` },
                            { replace: true },
                          )
                  }
                  decryptKey={decryptKey}
                  key={`${messageId}_${index}`}
                  attach={attach}
                  hasAccess={!isHidden}
                />
              );
            })}
          </div>
          <MediaViewer
            decryptKey={decryptKey}
            active={isMediaViewer}
            startFrom={openMediaId}
            attachments={attachments}
            onClose={() => setIsMediaViewer(false)}
          />
        </>
      )}
      {isNothing && (
        <div className={'media__nothing'}>
          <AnimatedEmoji size={135} emoji="🕸" />
          <h3 className={'media__yet'}>{t('media_nothing_title')}</h3>
          <p className={'media__later'}>{t('media_nothing_later')}</p>
        </div>
      )}
    </ScrollView>
  );
};
